import { useEffect, useMemo, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { Printer, ArrowLeft } from 'lucide-react'
import { getInhouseRequest, type InhouseRequest } from '@/services/inhouseService'
import { JOSCOWORK_COMPANY, INHOUSE_DAILY_RATE } from '@/lib/joscoworkCompany'
import { thaiBahtText } from '@/lib/thaiBahtText'

// ใบแจ้งหนี้ In-house — เทมเพลตเดียวกับใบเสนอราคา (InhouseRequest/index.tsx) แต่ตัดข้อกำหนด+ลายเซ็นออก
// เน้นบัญชีธนาคารให้เด่น (จุดประสงค์คือบอกลูกค้าว่าต้องโอนเงินไปที่ไหน) — ใช้ request_number เดิม ไม่ออกเลขแยก
// เข้าดูได้เฉพาะคำขอที่ accepted_at ไม่ว่างและมี billing_info (ติ๊กขอใบแจ้งหนี้ไว้ตอนยอมรับ) เท่านั้น

const formatDate = (value: string) =>
  new Date(value).toLocaleDateString('th-TH', { day: 'numeric', month: 'long', year: 'numeric' })
const formatDateShort = (value: string) =>
  new Date(value).toLocaleDateString('th-TH', { day: 'numeric', month: 'short', year: '2-digit' })

const money = (n: number) => n.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

function countDays(dateFrom: string, dateTo: string): number {
  const from = new Date(dateFrom)
  const to = new Date(dateTo)
  const diff = Math.round((to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24))
  return Math.max(1, diff + 1)
}

const PRINT_CSS = `
@media print {
  @page { size: 210mm 297mm; margin: 14mm; }
  html, body { height: auto !important; overflow: visible !important; }
  body * { visibility: hidden; }
  .invoice-print-area, .invoice-print-area * { visibility: visible; }
  .invoice-print-area * {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
  .invoice-print-area {
    position: absolute !important;
    left: 0; top: 0;
    width: 100% !important;
    max-width: none !important;
    margin: 0 !important;
    border: none !important;
    box-shadow: none !important;
    padding: 0 !important;
    min-height: calc(297mm - 28mm) !important;
    height: auto !important;
  }
}
`

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between text-sm">
      <span>{label}</span>
      <span className="font-medium">{value}</span>
    </div>
  )
}

const recipientDisplay = (request: InhouseRequest) => {
  const info = request.billing_info
  if (!info) return { name: request.company_name || request.contact_name, address: '', taxId: '' }
  if (info.recipient_type === 'COMPANY') {
    return {
      name: info.company_name || request.company_name || '-',
      address: [info.address, info.subdistrict, info.district, info.province, info.postal_code].filter(Boolean).join(' '),
      taxId: info.tax_id ? `${info.tax_id}${info.branch_code ? ` (สาขา ${info.branch_code})` : ''}` : '',
    }
  }
  return {
    name: info.full_name || request.contact_name,
    address: [info.address, info.subdistrict, info.district, info.province, info.postal_code].filter(Boolean).join(' '),
    taxId: info.tax_id || '',
  }
}

export default function InhouseInvoiceView() {
  const { id } = useParams<{ id: string }>()
  const [request, setRequest] = useState<InhouseRequest | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    const style = document.createElement('style')
    style.id = 'invoice-print-style'
    style.textContent = PRINT_CSS
    document.head.appendChild(style)
    return () => style.remove()
  }, [])

  useEffect(() => {
    if (!id) return
    getInhouseRequest(id)
      .then((req) => {
        if (!req.accepted_at || !req.billing_info) throw new Error('คำขอนี้ไม่มีใบแจ้งหนี้')
        setRequest(req)
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'ไม่พบใบแจ้งหนี้'))
      .finally(() => setLoading(false))
  }, [id])

  const days = request ? countDays(request.date_from, request.date_to) : 0
  const unitPrice = request?.unit_price ?? INHOUSE_DAILY_RATE
  const subtotal = request?.total_price ?? days * unitPrice
  const vat = Math.round(subtotal * 0.07 * 100) / 100
  const withholding = Math.round(subtotal * 0.03 * 100) / 100
  const grandTotal = subtotal + vat - withholding

  const dateRangeText = useMemo(() => {
    if (!request) return ''
    return request.date_from === request.date_to
      ? formatDateShort(request.date_from)
      : `${formatDateShort(request.date_from)} - ${formatDateShort(request.date_to)}`
  }, [request])

  const recipient = request ? recipientDisplay(request) : null

  return (
    <div className="min-h-screen bg-[#FAFAFB] pb-16 pt-24">
      <div className="mx-auto max-w-4xl px-4 md:px-6">
        <div className="mb-6 flex items-center justify-between print:hidden">
          <Link to={`/inhouse-requests/${id}`} className="flex items-center gap-1.5 text-sm text-text-muted hover:text-accent">
            <ArrowLeft className="h-4 w-4" />
            กลับไปใบเสนอราคา
          </Link>
          {request && (
            <button
              onClick={() => window.print()}
              className="flex items-center gap-2 rounded-xl bg-gradient-to-r from-accent to-accent2 px-4 py-2.5 text-sm font-semibold text-white shadow-lg shadow-accent/25 transition-all hover:shadow-accent/40"
            >
              <Printer className="h-4 w-4" />
              ดาวน์โหลด PDF
            </button>
          )}
        </div>

        {loading ? (
          <p className="text-sm text-text-muted">กำลังโหลด...</p>
        ) : error ? (
          <p className="text-sm text-rose-500">{error}</p>
        ) : request && recipient ? (
          <div
            className="invoice-print-area mx-auto flex flex-col border border-black bg-white p-8 text-[#000] md:p-12"
            style={{ fontFamily: "'Sarabun', sans-serif", width: '210mm', minHeight: '297mm' }}
          >
            <div className="mb-6 text-center">
              <h1 className="text-2xl font-bold">ใบแจ้งหนี้</h1>
              <p className="text-xs">INVOICE</p>
              <p className="mt-1 text-xs">วันที่ {formatDate(request.created_date)}</p>
            </div>

            <div className="mb-6 grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div>
                <p className="text-sm">เรียน &nbsp; {recipient.name}</p>
                {recipient.address && <p className="mt-1 text-xs">{recipient.address}</p>}
                {recipient.taxId && <p className="text-xs">เลขประจำตัวผู้เสียภาษี {recipient.taxId}</p>}
              </div>
              <div className="border border-black px-4 py-3 text-xs">
                <p className="font-bold">{JOSCOWORK_COMPANY.name}</p>
                <p>{JOSCOWORK_COMPANY.addressLine1}</p>
                <p>{JOSCOWORK_COMPANY.addressLine2}</p>
                <p>Tel. {JOSCOWORK_COMPANY.tel} · เลขผู้เสียภาษี {JOSCOWORK_COMPANY.taxId}</p>
              </div>
            </div>

            <table className="mb-4 w-full border-collapse text-left text-sm">
              <thead>
                <tr className="border-b-2 border-black">
                  <th className="w-10 py-2 pl-1 text-xs font-semibold">ลำดับ</th>
                  <th className="py-2 text-xs font-semibold">รายการ</th>
                  <th className="w-20 py-2 text-right text-xs font-semibold">จำนวน</th>
                  <th className="w-24 py-2 text-right text-xs font-semibold">ราคา/วัน</th>
                  <th className="w-28 py-2 pr-1 text-right text-xs font-semibold">จำนวนเงิน</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-black align-top">
                  <td className="py-2.5 pl-1">1</td>
                  <td className="py-2.5">
                    หลักสูตร {request.course?.title ?? '-'}
                    <br />
                    <span className="text-xs">อบรมวันที่ {dateRangeText} · ผู้เข้าอบรม {request.trainee_count} คน · {request.location}</span>
                  </td>
                  <td className="py-2.5 text-right">{days} วัน</td>
                  <td className="py-2.5 text-right">{money(unitPrice)}</td>
                  <td className="py-2.5 pr-1 text-right">{money(subtotal)}</td>
                </tr>
              </tbody>
            </table>

            <div className="mb-4 flex justify-end">
              <div className="w-64 space-y-1.5 border border-black px-4 py-3">
                <Row label="รวมเป็นเงิน" value={money(subtotal)} />
                <Row label="ภาษีมูลค่าเพิ่ม 7%" value={money(vat)} />
                <Row label="หัก ณ ที่จ่าย 3%" value={`-${money(withholding)}`} />
                <div className="border-t-2 border-black pt-1.5">
                  <div className="flex justify-between font-bold">
                    <span>รวมเงินทั้งสิ้น</span>
                    <span>฿{money(grandTotal)}</span>
                  </div>
                </div>
              </div>
            </div>

            <p className="mb-6 text-sm">
              จำนวนเงิน (ตัวอักษร) &nbsp;
              <span className="font-medium">{thaiBahtText(grandTotal)}</span>
            </p>

            {/* เน้นบัญชีธนาคารให้เด่น — จุดประสงค์หลักของใบแจ้งหนี้คือบอกว่าจะโอนเงินไปที่ไหน (ต่างจากใบเสนอราคาที่มีลายเซ็น) */}
            <div className="mt-auto">
              <div className="mb-8 border-2 border-black p-4 text-sm">
                <p className="mb-1 font-semibold">ช่องทางการชำระเงิน</p>
                <p>โอนเงินเข้าบัญชี ชื่อบัญชี {JOSCOWORK_COMPANY.name}</p>
                <p>{JOSCOWORK_COMPANY.bankName} เลขที่บัญชี {JOSCOWORK_COMPANY.bankAccountNumber}</p>
              </div>

              <p className="border-t border-black pt-4 text-center text-[11px]">
                เอกสารนี้ออกโดยระบบ JosCoWork · {request.request_number}
              </p>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  )
}
